import { DropdownField } from "@/components/ui/form/DropdownField"
import { useEffect, useRef, useState } from "react"
import { useEffectOnce } from "react-use"
import { getProjects, getProject, getProjectOperations } from "@/fetch/projects"
import { District, Project } from "src/types"
import {
  Control,
  FieldValues,
  Path,
  UseFormReturn,
  useWatch,
} from "react-hook-form"
import { LoaderCircle } from "lucide-react"
import styled from "styled-components"

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(2, calc(50% - 8px));
  gap: 16px;
`

export type EditTask = {
  timeUsed: { hours: number; minutes: number }
  unitsUsed: string
  salaryUsed: number
  projectId: [number]
  subprojectId?: [number] | null
  operationId?: [number]
  date: string
}
type OpGroupItem = { id: number; name: string; unitsPredicted: number }

type Props<T extends FieldValues = FieldValues> = {
  control: Control<T>
  form: UseFormReturn<EditTask, undefined, undefined>
  onOperationChange?: (op: OpGroupItem | undefined) => void
}

export function EditTaskPosition<T extends FieldValues = FieldValues>({
  control,
  form,
  onOperationChange,
}: Props<T>) {
  const [projects, setProjects] = useState<Project[]>([])
  const [districts, setDistricts] = useState<District[]>([])
  const [opGroups, setOpGroups] = useState<OpGroupItem[]>([])
  const [fetched, setFetched] = useState({ projects: false, districts: false })

  const [projectId] = useWatch({
    control: form.control,
    name: "projectId",
    defaultValue: form.getValues("projectId"),
  })
  const [subprojectId] =
    useWatch({
      control: form.control,
      name: "subprojectId",
      defaultValue: form.getValues("subprojectId"),
    }) ?? []

  const [operationId] =
    useWatch({
      control: form.control,
      name: "operationId",
      defaultValue: form.getValues("operationId"),
    }) ?? []

  useEffect(() => {
    if (operationId && opGroups.length) {
      const op = opGroups.find((g) => g.id === operationId)
      onOperationChange?.(op)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operationId, opGroups])

  useEffectOnce(() => {
    getProjects({}).then(async (res) => {
      if (res) {
        setProjects(res.items)

        const districtsRes = await getProject(projectId)
        setDistricts(districtsRes.items)
        if (subprojectId) {
          const opRes = await getProjectOperations(projectId, subprojectId)
          const groups = opRes.groups.flatMap((group: any) =>
            group.operations.map((op: any) => ({
              id: op.id,
              name: `${group.name} / ${op.name}`,
              unitsPredicted: op.unitsPredicted ?? 0,
            }))
          )
          setOpGroups(groups)
        }
        setFetched((prev) => ({ districts: true, projects: true }))
      }
    })
  })

  const prev = useRef<{ projectId: number; subprojectId?: number }>()

  if (!prev.current) {
    prev.current = { projectId, subprojectId }
  }

  async function callUpdates(projectId: number, subprojectId?: number) {
    const projectChanged = prev.current?.projectId !== projectId
    const subprojectChanged = prev.current?.subprojectId !== subprojectId

    if (!projectId) {
      return
    }

    if (projectChanged) {
      const res = await getProject(projectId)
      // setting null
      form.setValue("subprojectId", null, {
        shouldDirty: true,
        shouldValidate: false,
      })
      setDistricts(res.items)
      setOpGroups([])
      setFetched((prev) => ({ ...prev, districts: true }))
      // stop updates here, we have cleared subproject and updated it's list
      prev.current = {
        projectId,
        subprojectId,
      }
      return
    }

    if (!subprojectId) {
      return
    }
    if (!subprojectChanged) {
      // if it's not changed, we don't care what happends next
      return
    }

    const res = await getProjectOperations(projectId, subprojectId)

    const groups = res.groups.flatMap((group: any) =>
      group.operations.map((op: any) => ({
        id: op.id,
        name: `${group.name} / ${op.name}`,
        unitsPredicted: op.unitsPredicted ?? 0,
      }))
    )
    setOpGroups(groups)
    form.setValue("operationId", undefined, {
      shouldDirty: true,
      shouldValidate: true,
    })
    onOperationChange?.(undefined)
  }
  useEffect(() => {
    callUpdates(projectId, subprojectId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, subprojectId])

  if (!fetched.districts || !fetched.projects) {
    return (
      <div className="flex items-center justify-center w-full p-16">
        <LoaderCircle className="animate-spin" />
      </div>
    )
  }

  return (
    <Row className="mt-12 mb-16">
      <DropdownField
        control={form.control}
        name={"projectId"}
        placeholder="Sērija"
        label="Sērija"
        options={projects.map((project) => ({
          id: project.id,
          name: project.name,
        }))}
      />
      <DropdownField
        // reset field when project changes
        key={projectId}
        control={form.control}
        name={"subprojectId"}
        placeholder="Iecirknis"
        label="Iecirknis"
        options={districts.map((district) => ({
          id: district.id,
          name: district.name,
        }))}
      />
      <DropdownField
        // reset field when subproject changes
        key={subprojectId}
        control={control}
        disabled={!subprojectId}
        name={"operationId" as Path<T>}
        placeholder="Operācija"
        label="Operācija"
        options={opGroups}
      />
    </Row>
  )
}
